import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useAppForm } from "@/components/form/form-context";
import { GroupedSettingTypeResponse, settingTypesQueryOptions, systemSettingsQueryOptions } from "@/server/settings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SettingListResponse, SettingType } from "@/lib/mma/types.gen";
import { CardContent } from "@/components/ui/card";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { SettingsFields } from "../../../components/settings-fields";
import { Filter } from "./-components/settings-toolbar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import _ from "lodash";
import { updateSystemSettingsFn } from "@/server/settings";
import { useScrollHashUpdater, highlightText } from "@/lib/utils";
import { ResponsiveActionBar } from "@/components/form/responsive-form";
import { toast } from "sonner";
import { clearGdprFn } from "@/server/gdpr";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_dashboard/settings")({
  component: RouteComponent,
  loader: async () => {
    return {
      crumb: "Settings",
    };
  },
});

function RouteComponent() {
  const settingTypesQuery = useQuery(settingTypesQueryOptions);
  const settingTypesData = settingTypesQuery.data as GroupedSettingTypeResponse;
  const systemSettingsQuery = useQuery(systemSettingsQueryOptions);
  const systemSettings = systemSettingsQuery.data as SettingListResponse;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [processedChoiceTypes, setProcessedChoiceTypes] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveAffectedItems, setSaveAffectedItems] = useState<string[]>([]);
  const [pendingPayload, setPendingPayload] = useState<Record<string, any> | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    section?: string;
  };

  const search = searchParams.search ?? "";
  const section = searchParams.section ?? "";

  // Debounce the URL search param to avoid filtering on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);
  useEffect(() => {
    setIsSearching(true);
    const id = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);
    return () => {
      clearTimeout(id);
      setIsSearching(false);
    };
  }, [search]);

  const updateSystemSettingsMutation = useMutation({
    mutationFn: updateSystemSettingsFn,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["systemSettings"] });
      setIsSubmitting(false);
      setSaveDialogOpen(false);
      form.reset(defaultSystemValues);
    },
    onError: (error) => {
      console.error("Failed to update system settings:", error);
      toast.error("Failed to update system settings. Please try again.");
      setIsSubmitting(false);
    },
  });

  const settingTypesRef = useRef<{
    value: any[];
    dependency: any;
  }>({ value: [], dependency: null });

  useEffect(() => {
    if (settingTypesQuery.isError) {
      const message =
        settingTypesQuery.error instanceof Error
          ? settingTypesQuery.error.message
          : "Failed to load setting types. Please try again later.";
      toast.error(message);
    }
  }, [settingTypesQuery.isError, settingTypesQuery.error]);

  if (settingTypesData && settingTypesRef.current.dependency !== settingTypesData.data) {
    settingTypesRef.current = {
      value:
        settingTypesData.data
          ?.map((group) => ({
            ...group,
            settings: group.settings?.filter(
              (setting) => ["SYSTEM", "ORGANISATION", "QUEUE"].includes(setting.domain) && setting.visibleOnSystem
            ) as unknown as (SettingType & {
              name: string;
            })[],
          }))
          .filter((group) => group.settings?.length > 0)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) || [],
      dependency: settingTypesData.data,
    };
  }

  const settingTypes = settingTypesRef.current.value;

  const filteredSettings = useMemo(() => {
    const lowerSearch = debouncedSearch.toLowerCase();
    return (settingTypes || [])
      .map((group) => ({
        ...group,
        settings: group.settings?.filter(
          (setting) =>
            setting.displayName.toLowerCase().includes(lowerSearch) ||
            setting.name.toLowerCase().includes(lowerSearch) ||
            (setting.description?.toLowerCase().includes(lowerSearch) ?? false)
        ),
      }))
      .filter((group) => (group.settings?.length ?? 0) > 0);
  }, [settingTypes, debouncedSearch]);

  const defaultSystemValuesRef = useRef<{
    value: Record<string, any>;
    settingTypes: any[];
    systemSettings: any;
  }>({
    value: {},
    settingTypes: [],
    systemSettings: null,
  });

  if (
    defaultSystemValuesRef.current.settingTypes !== settingTypes ||
    defaultSystemValuesRef.current.systemSettings !== systemSettings
  ) {
    defaultSystemValuesRef.current = {
      value: Object.fromEntries(
        settingTypes
          .flatMap((group) => group.settings)
          .filter((setting) => Object.hasOwn(systemSettings, setting.name))
          .map((setting) => [setting.name, systemSettings[setting.name]])
      ),
      settingTypes: settingTypes,
      systemSettings,
    };
  }

  const defaultSystemValues = defaultSystemValuesRef.current.value;

  const settingsGroups = useRef<string[]>([]);
  settingsGroups.current = settingTypes.map((settingType) => settingType.displayName);

  // const sectionNames = useMemo(() => {
  //   return settingTypes.map((settingType) => settingType.displayName);
  // }, [settingTypes]);
  // useScrollHashUpdater(sectionNames);

  // Scroll to section when section parameter changes
  useEffect(() => {
    if (section && typeof window !== "undefined") {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [section]);

  function buildSearchParams(overrides: Partial<Record<string, any>>) {
    const params: Record<string, any> = {
      search,
      section,
      ...overrides,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === "") {
        delete params[key];
      }
    });

    return params;
  }

  useEffect(() => {
    if (!settingTypes) return;

    const uniqueChoiceTypes = new Set<string>();

    //Some selects don't come with predefined choices and rely on other data fetched from the server to populate the options.
    //This code takes the name for those choices and matches it with a query key to take the data from that query.
    settingTypes.forEach((settingGroup: any) => {
      settingGroup.settings.forEach((setting: any) => {
        if (
          setting?.validation?.type === "choices" &&
          !setting?.validation?.choices?.predefinedChoices.length &&
          typeof setting?.validation?.choices?.choiceType === "string" &&
          setting.validation.choices.choiceType
        ) {
          uniqueChoiceTypes.add(setting.validation.choices.choiceType as string);
        }
      });
    });

    if (uniqueChoiceTypes.size === 0) return;

    // Load choices for each type that hasn't been processed yet.
    uniqueChoiceTypes.forEach((choiceType) => {
      if (!processedChoiceTypes.has(choiceType)) {
        // loadChoicesForType(choiceType);
        setProcessedChoiceTypes((prev) => new Set(prev).add(choiceType));
      }
    });
  }, [settingTypes]);
  const form = useAppForm({
    defaultValues: {
      ...defaultSystemValues,
    } as any,
  });

  // Listen for a forced reset from the sidebar when clicking Settings while already on the page
  useEffect(() => {
    function handleForceReset() {
      navigate({ to: "/settings", replace: true, search: {}, hash: undefined });
      form.reset({ ...defaultSystemValues } as any);
      const mainContentContainer = document.getElementById("main-content-container");
      requestAnimationFrame(() => {
        mainContentContainer?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      });
    }
    window.addEventListener("forceSettingsReset", handleForceReset);
    return () => window.removeEventListener("forceSettingsReset", handleForceReset);
  }, [navigate, defaultSystemValues, form]);

  const handleFilterChange = useCallback(
    (filters: { search?: string; organisation?: string; expire?: string; hash?: string }) => {
      navigate({
        to: "/settings",
        replace: true,
        search: () =>
          buildSearchParams({
            search: filters.search?.trim() || undefined,
          }),
      });
    },
    [navigate, buildSearchParams]
  );

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const values = form.state.values;
    const settingBody: Record<string, any> = {};
    Object.keys(values).forEach((key) => {
      if (values[key] !== defaultSystemValues[key]) {
        settingBody[key] = values[key];
      }
    });

    const changedKeys = Object.keys(settingBody);
    const items: string[] = [];
    settingTypes.forEach((group: any) => {
      group.settings?.forEach((setting: any) => {
        if (changedKeys.includes(setting.name)) {
          items.push(setting.displayName);
        }
      });
    });

    setSaveAffectedItems(items);
    setPendingPayload(settingBody);
    setSaveDialogOpen(true);
  };

  const confirmSave = async () => {
    if (!pendingPayload) return;
    setIsSubmitting(true);
    const updateSystemSettingsPromise = updateSystemSettingsMutation.mutateAsync({
      data: {
        payload: pendingPayload,
      },
    });
    toast.promise(updateSystemSettingsPromise, {
      loading: "Updating system settings...",
      success: "System settings updated successfully",
      error: (error) => error.message || "Failed to update system settings",
    });
  };

  const clearGdprMutation = useMutation({
    mutationFn: clearGdprFn,
    onError: (error) => {
      console.error("Failed to clear GDPR:", error);
    },
  });

  const clearGdpr = () => {
    const clearGdprPromise = clearGdprMutation.mutateAsync({
      data: {
        orgId: undefined,
      },
    });
    toast.promise(clearGdprPromise, {
      loading: "Clearing GDPR...",
      success: "GDPR cleared successfully",
      error: (error) => error.message || "Failed to clear GDPR",
    });
  };

  return (
    <>
      <DashboardLayout
        submenu={{
          items: filteredSettings.map((settingGroup) => ({
            label: settingGroup.displayName,
            href: `#${settingGroup.displayName?.toLowerCase().replace(/ /g, "-")}`,
            id: settingGroup.displayName,
            name: settingGroup.displayName,
          })),
          title: "Setting Groups",
          active: true,
        }}
      >
        {isSearching && (
          <div className="absolute top-0 left-0 w-full h-full bg-background/80 z-10 flex justify-center">
            <Badge className="h-10 mt-36" variant="secondary">
              <Spinner />
              Searching...
            </Badge>
          </div>
        )}
        <form
          onSubmit={(e) => {
            //Some black magic occurs in TanStack's form.handleSubmit() that causes a massive performance hit when submitting the form.
            //We run this manual submit instead as a workaround.
            handleFormSubmit(e);
          }}
        >
          <div className="flex flex-col gap-2 relative mb-5">
            <div className="sticky top-0 z-10 bg-background">
              <Filter onFilterChange={handleFilterChange} defaultSearch={search} items={settingTypes} />
            </div>
            {filteredSettings.map((settingType) => (
              <Card
                id={settingType.displayName?.toLowerCase().replace(/ /g, "-")}
                key={settingType.groupId}
                className="settings-category"
              >
                <CardHeader className="flex items-center">
                  <CardTitle>{highlightText(settingType.displayName, debouncedSearch)}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  <SettingsFields
                    settings={settingType.settings}
                    form={form}
                    processedChoiceTypes={processedChoiceTypes}
                    searchTerm={debouncedSearch}
                    searchAvailable={true}
                    clearGDPR={clearGdpr}
                    isSystemSettings={true}
                  />
                </CardContent>
              </Card>
            ))}
            {filteredSettings.length === 0 && (
              <Card>
                <CardContent>
                  <p className="text-md pb-20 pt-15 text-center">No results found.</p>
                </CardContent>
              </Card>
            )}
          </div>
          <form.Subscribe selector={(s) => [s.isDirty, s.canSubmit, s.values]}>
            {([isDirty, canSubmit, values]) => {
              const hasChanges = Object.keys(values as any).some(
                (key) => (values as any)[key] !== defaultSystemValues[key]
              );
              return (
                <ResponsiveActionBar>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isSubmitting || !hasChanges || !canSubmit}>
                      {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCancelDialogOpen(true);
                      }}
                      disabled={isSubmitting || !hasChanges || !canSubmit}
                    >
                      Reset
                    </Button>
                  </div>
                </ResponsiveActionBar>
              );
            }}
          </form.Subscribe>
        </form>
      </DashboardLayout>
      <ConfirmDialog
        open={saveDialogOpen}
        title="Save settings"
        description="Are you sure you want to save the following changes?"
        affectedItems={saveAffectedItems}
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={confirmSave}
        onCancel={() => {
          setSaveDialogOpen(false);
        }}
        confirmVariant="default"
        loadingLabel="Saving..."
        loading={updateSystemSettingsMutation.isPending}
      />
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard changes"
        description="Are you sure you want to undo all unsaved changes? This will reset the form back to its initial state."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={() => {
          window.dispatchEvent(new CustomEvent("forceSettingsReset"));
          setCancelDialogOpen(false);
        }}
        onCancel={() => {
          setCancelDialogOpen(false);
        }}
      />
    </>
  );
}
